import React from "react";
import {CancelToken} from "axios";
import Api from "../../../core/services/api/api";
import useIsMounted from "../use-is-mounted";
import CacheService from "../../../core/services/cache/cache_service";
import {useAuthenticationState} from "../use-auth-state";
import LocalStorageService from "../../../core/services/cache/local_storage_service";

export interface PartnerInformationConfiguration {
    PartnerLogoUrlBaseAddress?: string;
}

export interface PartnerInformation {
    id: string;
    name: string;
    description?: string;
    fullAddress?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    phone?: string;
    fax?: string;
    province?: string;
    logo?: string;
    website?: string;
    colorThemeJson?: string;
    saleRegulationDescription?: string;
    salePromotionalText?: string;
    configuration: PartnerInformationConfiguration;
}

export interface PartnerInformationController {
    getPartnerInformation: (args?: GetPartnerInformationArgs, cancelToken?: CancelToken) => Promise<PartnerInformation | undefined>;
    setCachedPartnerInformation: React.Dispatch<React.SetStateAction<PartnerInformation | null>>;
}

const PartnerInformationStateContext = React.createContext<PartnerInformation | null>(null);
const partnerInformationControllerContext = React.createContext<PartnerInformationController>({
    getPartnerInformation: async () => undefined,
    setCachedPartnerInformation: async () => undefined,
});


export interface PartnerInformationProviderProps {
    setLocalState?: React.Dispatch<React.SetStateAction<PartnerInformation | null>>;
}

export interface GetPartnerInformationArgs {
    customAuthToken?: string;
    logger?: unknown;
    disregardCache?: boolean;
    setGlobalState?: boolean;
    dispatchEvent?: boolean;
}

const PartnerInformationProvider: React.FC<React.PropsWithChildren<PartnerInformationProviderProps>> = ({children, setLocalState}) => {
    const {loggedIn} = useAuthenticationState();
    const [state, _setState] = React.useState<PartnerInformation | null>(null);
    const stateRef = React.useRef(state);
    /** Dedupes concurrent default-auth partner-info fetches onto one in-flight request. */
    const inFlightRef = React.useRef<Promise<PartnerInformation | undefined> | null>(null);
    /** True while this provider writes operatorPartnerId so the storage listener ignores the same-tab echo. */
    const isSettingPartnerIdRef = React.useRef(false);
    const isMounted = useIsMounted();

    /**
     * Sets the state of the partner information.
     */
    const setState = React.useCallback((partnerInformation: React.SetStateAction<PartnerInformation | null>) => {
        _setState(prev => {
            const newState = typeof partnerInformation === "function" ? partnerInformation(prev) : partnerInformation;
            stateRef.current = newState;
            return newState;
        });
    }, [])

    /**
     * Sets the state of the partner information to the local state if it is provided.
     */
    React.useEffect(() => {
        if (setLocalState) {
            setLocalState(state);
        }
    }, [setLocalState, state])

    /**
     * Applies a successful partner-information API response to state / partner-id cache.
     */
    const applyPartnerInformationResponse = React.useCallback((
        response: Awaited<ReturnType<typeof Api.getPartnerInformation>>,
        {setGlobalState, dispatchEvent}: Pick<GetPartnerInformationArgs, 'setGlobalState' | 'dispatchEvent'>,
    ): PartnerInformation | undefined => {
        if (!isMounted()) {
            return;
        }
        const result = ({
            ...(response?.data ?? {}),
            configuration: response?.configuration ?? {},
        }) as PartnerInformation;
        if (response?.resultFlag && setGlobalState !== false) {
            setState(result);
        }
        if (response?.resultFlag && result.id && dispatchEvent !== false) {
            if (CacheService.getOperatorPartnerId() !== result.id) {
                isSettingPartnerIdRef.current = true;
                try {
                    CacheService.setOperatorPartnerId(result.id);
                } finally {
                    isSettingPartnerIdRef.current = false;
                }
            }
        }
        return result;
    }, [isMounted, setState]);

    /**
     * Fetches partner information (logo, theme, profile). Reuses cache and dedupes concurrent default-auth calls.
     * Custom-auth callers do not share the default in-flight promise; shared fetches ignore cancel tokens so
     * unmounting an autoFetch consumer cannot abort the provider-owned load.
     */
    const getPartnerInformation = React.useCallback<PartnerInformationController['getPartnerInformation']>(async ({
                                                                                                                      customAuthToken,
                                                                                                                      logger,
                                                                                                                      disregardCache,
                                                                                                                      setGlobalState,
                                                                                                                      dispatchEvent
                                                                                                                  } = {}, cancelToken) => {
        if (customAuthToken) {
            const response = await Api.getPartnerInformation(
                () => ({Authorization: customAuthToken}),
                cancelToken,
                true,
                logger,
            );
            return applyPartnerInformationResponse(response, {setGlobalState, dispatchEvent});
        }

        if (!disregardCache && !!stateRef.current) {
            return stateRef.current;
        }
        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        const request = (async () => {
            try {
                const response = await Api.getPartnerInformation(
                    null,
                    null,
                    false,
                    logger,
                );
                return applyPartnerInformationResponse(response, {setGlobalState, dispatchEvent});
            } finally {
                inFlightRef.current = null;
            }
        })();

        inFlightRef.current = request;
        return request;
    }, [applyPartnerInformationResponse])

    /**
     * Fetches the partner information when the provider is mounted / user becomes logged in.
     * Also refetches when another tab changes the operator partner id (ignores same-tab echo from setOperatorPartnerId).
     */
    React.useEffect(() => {
        if (!loggedIn)
            return;
        void getPartnerInformation();
        const storageListener = (e: StorageEvent) => {
            if (e.key !== LocalStorageService.keys.operatorPartnerId)
                return;
            if (isSettingPartnerIdRef.current)
                return;
            if (e.newValue === e.oldValue)
                return;
            void getPartnerInformation({disregardCache: true, dispatchEvent: false});
        }
        window.addEventListener('storage', storageListener);
        return () => {
            window.removeEventListener('storage', storageListener);
        }
    }, [getPartnerInformation, loggedIn])


    const controller = React.useMemo<PartnerInformationController>(() => ({
        getPartnerInformation,
        setCachedPartnerInformation: setState,
    }), [getPartnerInformation, setState]);

    return (
        <>
            <partnerInformationControllerContext.Provider value={controller}>
                <PartnerInformationStateContext.Provider value={state}>
                    {children}
                </PartnerInformationStateContext.Provider>
            </partnerInformationControllerContext.Provider>
        </>
    );
}

export default PartnerInformationProvider;

export const usePartnerInformation = () => React.useContext(PartnerInformationStateContext);

export const usePartnerInformationController = () => React.useContext(partnerInformationControllerContext);